import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/shared/Avatar';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useFlipPosition } from '@/hooks/useFlipPosition';
import { IconChevronDown, IconLogout, IconUsers } from '@/lib/icons';
import type { Role, User } from '@/types';

interface AccountMenuProps {
  user: User;
  onLogout: () => void;
}

// Role → StatusBadge semantic color: admin stands out (accent), editor reads as
// an active contributor (success), viewer as read-only (neutral).
const ROLE_STATUS: Record<Role, string> = {
  admin: 'info',
  editor: 'success',
  viewer: 'neutral',
};

// AccountMenu turns the header avatar cluster into a real account dropdown:
// identity, an admin-only shortcut into team management, and Log out. It is the
// conventional home for "who am I / my team / sign out" — so team management is
// reachable without adding a sidebar nav item. Anchored via useFlipPosition and
// closed on outside-click / Escape, mirroring SiteSwitcherMenu.
export function AccountMenu({ user, onLogout }: AccountMenuProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const isAdmin = user.role === 'admin';

  const close = useCallback(() => setOpen(false), []);
  const toggle = () => {
    setAnchorRect(triggerRef.current?.getBoundingClientRect() ?? null);
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const { top, left } = useFlipPosition({
    anchorRect,
    menuRef,
    defaultMenuHeight: 180,
    defaultMenuWidth: 240,
  });

  const goToTeam = () => {
    close();
    navigate('/settings?tab=team');
  };

  const handleLogout = () => {
    close();
    onLogout();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex items-center gap-2 rounded-full pl-2 pr-1 py-1 hover:bg-dash-border/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <div className="text-right hidden sm:block">
          <div className="text-xs text-dash-primary">{user.name || user.email}</div>
          <div className="text-[10px] uppercase tracking-wide font-mono text-dash-muted">
            {user.role}
          </div>
        </div>
        <Avatar name={user.name} email={user.email} avatarUrl={user.avatar_url} />
        <IconChevronDown
          size={14}
          aria-hidden="true"
          className={`text-dash-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="Account"
              style={{ position: 'fixed', top, left, zIndex: 50 }}
              className="w-60 bg-dash-surface border border-dash-border rounded-md shadow-lg overflow-hidden"
            >
              <div className="px-3 py-3 border-b border-dash-border">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar name={user.name} email={user.email} avatarUrl={user.avatar_url} />
                  <div className="min-w-0">
                    <div className="text-sm text-dash-primary truncate">
                      {user.name || user.email}
                    </div>
                    <div className="text-[11px] text-dash-muted font-mono truncate">
                      {user.email}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <StatusBadge status={ROLE_STATUS[user.role]}>{user.role}</StatusBadge>
                </div>
              </div>

              {isAdmin ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={goToTeam}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-dash-secondary hover:bg-dash-bg/40 hover:text-dash-primary"
                >
                  <IconUsers size={16} aria-hidden="true" />
                  Team members
                </button>
              ) : null}

              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-dash-secondary hover:bg-dash-bg/40 hover:text-dash-primary border-t border-dash-border"
              >
                <IconLogout size={16} aria-hidden="true" />
                Log out
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
